// "Switch system" modal for the Energy app — opened by clicking the
// "Energy" badge under the Flex2 logo on the sidebar.
//
// Deliberately manual-only, unlike flex2-webapp's DefaultSystemPicker which
// also serves the blocking first-login "pick a default system" flow. Login
// happens in the flex2 app, so a user only ever reaches /energy/* after a
// mode is already resolved — a self-gating blocking modal here could only
// misfire.
//
// The other difference that matters: energy-webapp is a separate CRA app
// that nginx only serves under /energy. Routing to another system's home
// with react-router would ask this bundle for a path it has no route for,
// so a non-Energy pick leaves via window.location.replace (the same idiom
// the org switcher in DashboardNavbar/modal uses).
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import EnergySavingsLeafIcon from "@mui/icons-material/EnergySavingsLeaf";
import BuildIcon from "@mui/icons-material/Build";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/Search";
import SchoolIcon from "@mui/icons-material/School";
import toast from "react-hot-toast";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { modalContainerStyle } from "pages/components/common/Styles";
import { setMyAppMode } from "api/users.api";
import { getUser } from "redux-store/auth/actions";
import {
  APP_MODES,
  BACKOFFICE_ORG_ID,
  DEFAULT_ROUTES,
  SYSTEM_DISPLAY_NAMES,
  systemNameForAppMode,
} from "config/appMode";

/**
 * Where a given app-mode actually lands.
 *
 * Deliberately NOT config/appMode's getDefaultRoute: this app's copy of that
 * helper was pinned to Energy during the energy-webapp split, so it returns
 * the Energy route for every mode. That is correct for login landing here —
 * energy-webapp only ever serves /energy/* — but it makes it useless for a
 * switcher, which by definition needs the OTHER systems' routes.
 *
 * Mirrors flex2-webapp's getDefaultRoute precedence: the user's pinned home
 * for that system, then the backoffice-org special case, then the mode's
 * global default.
 */
const targetRouteForAppMode = (appMode, userDetails) => {
  const systemName = systemNameForAppMode(appMode);
  const pinned = systemName ? userDetails?.systemDefaultRoutes?.[systemName] : null;
  if (pinned) return pinned;

  if (appMode !== APP_MODES.ENERGY && userDetails?.organizationId === BACKOFFICE_ORG_ID) {
    return "/platform/health";
  }

  return DEFAULT_ROUTES[appMode] || DEFAULT_ROUTES[APP_MODES.FLEX2];
};

// Mirrors SYSTEM_MAP in flex2-webapp's DefaultSystemPicker — keep the two in
// step when a system is added, since both read the same
// OrganizationSystems.name strings off userDetails.organizationSystems.
const SYSTEM_MAP = {
  Environmental: {
    appMode: APP_MODES.AFMS,
    enabled: true,
    icon: <EnergySavingsLeafIcon fontSize="small" sx={{ color: "#2ed66f" }} />,
    label: "Environmental",
  },
  "LiftConnect Pro": {
    appMode: APP_MODES.LCP,
    enabled: true,
    icon: <CarRepairIcon fontSize="small" sx={{ color: "#d32f2f" }} />,
    label: "LiftConnect Pro",
  },
  "Fixed Operations": {
    appMode: APP_MODES.FLEX2,
    enabled: true,
    icon: <BuildIcon fontSize="small" sx={{ color: "#1976d2" }} />,
    label: "Fixed Operations",
  },
  Energy: {
    appMode: APP_MODES.ENERGY,
    enabled: true,
    icon: <BoltIcon fontSize="small" sx={{ color: "#ff9100" }} />,
    label: "Energy",
  },
  // Asset training qualification. Violet to match the TrainingPortal rail
  // accent (#7c3aed) — keep the two hexes identical or the icon here stops
  // reading as the same product as that sidebar.
  Training: {
    appMode: APP_MODES.TRAINING,
    enabled: true,
    icon: <SchoolIcon fontSize="small" sx={{ color: "#7c3aed" }} />,
    label: "Training",
  },
  // Generic asset-class inspections. Deep cyan to match the InspectionsPortal
  // rail accent (#0e7490) — keep the two hexes identical or the icon here
  // stops reading as the same product as that sidebar.
  Inspections: {
    appMode: APP_MODES.INSPECTIONS,
    enabled: true,
    icon: <SearchIcon fontSize="small" sx={{ color: "#0e7490" }} />,
    label: "Inspections",
  },
};

const buildOptions = (organizationSystems) => {
  const systems = Array.isArray(organizationSystems) ? organizationSystems : [];
  return systems.map((systemName) => {
    const entry = SYSTEM_MAP[systemName];
    if (entry) {
      return {
        systemName,
        label: entry.label,
        selectValue: entry.appMode,
        enabled: true,
        icon: entry.icon,
        hint: null,
      };
    }
    return {
      systemName,
      label: SYSTEM_DISPLAY_NAMES[systemName] || systemName,
      selectValue: systemName,
      enabled: false,
      icon: null,
      hint: "Coming soon",
    };
  });
};

function SwitchSystemModal({ open, onClose }) {
  const dispatch = useDispatch();
  const store = useStore();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const userDetails = user?.userDetails;

  const options = useMemo(
    () => buildOptions(userDetails?.organizationSystems),
    [userDetails?.organizationSystems]
  );
  const enabledOptions = useMemo(() => options.filter((o) => o.enabled), [options]);

  const initialSelection = useMemo(() => {
    const current = userDetails?.effectiveAppMode;
    if (enabledOptions.some((o) => o.selectValue === current)) return current;
    return enabledOptions[0]?.selectValue ?? "";
  }, [userDetails?.effectiveAppMode, enabledOptions]);

  const [selected, setSelected] = useState(initialSelection);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSelected(initialSelection);
    setError(null);
  }, [open, initialSelection]);

  const handleClose = () => {
    if (saving) return;
    if (onClose) onClose();
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      await setMyAppMode(selected);
      await dispatch(getUser());

      const label =
        SYSTEM_DISPLAY_NAMES[selected] ||
        options.find((o) => o.selectValue === selected)?.label ||
        selected;
      toast.success(`Switched to ${label}`);

      // Read fresh userDetails from the store — the closure copy predates
      // getUser() and would route off stale flags (notably the pinned-home
      // map, which setMyAppMode can change).
      const fresh = store.getState()?.auth?.user?.userDetails || userDetails || {};
      const target = targetRouteForAppMode(selected, fresh);

      if (selected === APP_MODES.ENERGY) {
        // Already in this bundle — client-side nav is enough.
        navigate(target);
        if (onClose) onClose();
      } else {
        // Fixed Ops, LiftConnect Pro and Environmental all live in
        // flex2-webapp, which nginx serves on a different upstream (:3000)
        // from this app (:3001, mounted at /energy). A react-router navigate
        // would ask THIS bundle for a route it doesn't have and fall through
        // to the /login catch-all, so the switch has to be a real page load.
        window.location.replace(target);
      }
    } catch (err) {
      console.error("Failed to switch system:", err);
      const message =
        err?.response?.data?.message || err?.message || "Couldn't switch system. Try again.";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open
      onClose={handleClose}
      aria-labelledby="switch-system-title"
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <SoftBox sx={modalContainerStyle}>
        <SoftTypography id="switch-system-title" variant="h4" component="h2" gutterBottom mb={1}>
          Switch system
        </SoftTypography>
        <SoftTypography variant="body2" fontWeight="regular" color="text" mb={2}>
          Pick the system you want to open. Disabled options are coming soon.
        </SoftTypography>

        <FormControl component="fieldset" fullWidth error={Boolean(error)}>
          <RadioGroup
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            aria-label="System"
          >
            {options.map((opt) => (
              <FormControlLabel
                key={opt.systemName}
                value={opt.selectValue}
                disabled={saving || !opt.enabled}
                // color="primary" — the SoftUI theme only paints the checked
                // visual for primary/secondary; other colors check in state
                // but render no dot.
                control={<Radio color="primary" />}
                sx={{ opacity: opt.enabled ? 1 : 0.5 }}
                label={
                  <SoftBox
                    component="span"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
                  >
                    {opt.icon || null}
                    <SoftTypography variant="button" fontWeight="medium">
                      {opt.label}
                    </SoftTypography>
                    {opt.hint ? (
                      <SoftTypography variant="caption" color="text" sx={{ opacity: 0.7, ml: 0.5 }}>
                        ({opt.hint})
                      </SoftTypography>
                    ) : null}
                  </SoftBox>
                }
              />
            ))}
          </RadioGroup>
          {error ? <FormHelperText>{error}</FormHelperText> : null}
          {enabledOptions.length === 0 ? (
            <FormHelperText>
              No selectable systems are available for your organization yet.
            </FormHelperText>
          ) : null}
        </FormControl>

        <SoftBox sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <SoftButton variant="outlined" color="dark" onClick={handleClose} disabled={saving}>
            Close
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="info"
            onClick={handleSave}
            disabled={saving || !selected || enabledOptions.length === 0}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Switching..." : "Switch"}
          </SoftButton>
        </SoftBox>
      </SoftBox>
    </Modal>
  );
}

SwitchSystemModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

export default SwitchSystemModal;
